import React, { useRef, useLayoutEffect, useState, useMemo } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const NodeBox = React.forwardRef(function NodeBox({ text, status, shouldBlink }, ref) {
  const statusClass =
    status === "critical" ? "status-critical" :
    status === "warning"  ? "status-warning"  :
    status === "healthy"  ? "status-healthy"  :
                            "status-default";

  return (
    <div ref={ref} className={`node-box ${statusClass}`}>
      <div className="node-text">{text}</div>
      {shouldBlink && <div className="node-circular-note">(Circular dependency)</div>}
    </div>
  );
});


const DownwardTreeView = ({ contractName, contractStatus, dependencies }) => {
  // outer scroll wrapper (keeps overflow behavior)
  const scrollRef = useRef(null);
  // inner container (padding etc.)
  const containerRef = useRef(null);
  // wrapper that we apply transform { translate + scale } to
  const scaleWrapperRef = useRef(null);

  const nodeRefs = useRef({});
  const connectionsRef = useRef([]);
  const [paths, setPaths] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  // pan & zoom states
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });

  connectionsRef.current = [];

  const root = useMemo(() => ({
    id: "_root_",
    name: contractName,
    status: contractStatus,
    dependencies: Array.isArray(dependencies) ? dependencies : []
  }), [contractName, contractStatus, dependencies]);

  const registerConnection = (fromKey, toKey) => {
    connectionsRef.current.push({ fromKey, toKey });
  };

  function RenderNode({ node, path = "0", visited = new Set() }) {
    if (!node) return null;
    const nodeId = node.id ?? node.name ?? `node-${path}`;
    const instanceKey = `${nodeId}#${path}`;
    const isRootNode = nodeId === "_root_";
    const shouldBlink = node.name?.trim() === contractName.trim() && !isRootNode;
    if (visited.has(nodeId)) {
      const assignRef = el => { if (el) nodeRefs.current[instanceKey] = el; };
      return (
        <div className="dtv-column">
          <div className={`dtv-node ${shouldBlink ? "node-blink" : ""}`}>
            <NodeBox ref={assignRef} text={node.name ?? nodeId} status={node.status} shouldBlink={shouldBlink} />
          </div>
        </div>
      );
    }

    const children = Array.isArray(node.dependencies) ? node.dependencies : [];

    children.forEach((child, idx) => {
      const childId = child.id ?? child.name ?? `child-${path}-${idx}`;
      const childKey = `${childId}#${path}-${idx}`;
      registerConnection(instanceKey, childKey);
    });

    return (
      <div className="dtv-column">
        <div className={`dtv-node ${shouldBlink ? "node-blink" : ""}`}>
          <NodeBox
            ref={el => { if (el) nodeRefs.current[instanceKey] = el; }}
            text={node.name ?? nodeId}
            status={node.status}
            shouldBlink={shouldBlink}
          />
        </div>

        {children.length > 0 && <div className="dtv-spacer" />}

        {children.length > 0 && (
          <div className="dtv-children">
            {children.map((child, idx) => (
              <div key={`${(child.id ?? child.name ?? `child-${idx}`)}#${path}-${idx}`} className="dtv-child">
                <RenderNode node={child} path={`${path}-${idx}`} visited={new Set([...visited, nodeId])} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // pan/zoom handlers
  const handleZoomIn = () => setZoom(z => Math.min(2.5, +(z * 1.2).toFixed(3)));
  const handleZoomOut = () => setZoom(z => Math.max(0.4, +(z / 1.2).toFixed(3)));
  const handleReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // drag handlers (mouse & touch)
  const startDrag = (clientX, clientY) => {
    setIsDragging(true);
    dragStartRef.current = { x: clientX, y: clientY };
    panStartRef.current = { ...pan };
    document.body.style.userSelect = "none";
  };
  const onPointerDown = (e) => {
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null) return;
    startDrag(cx, cy);
  };
  const onPointerMove = (e) => {
    if (!isDragging) return;
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null) return;
    const dx = cx - dragStartRef.current.x;
    const dy = cy - dragStartRef.current.y;
    setPan({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };
  const onPointerUp = () => {
    setIsDragging(false);
    document.body.style.userSelect = "";
  };

  /**
   * IMPORTANT:
   * Compute path coords relative to the scaleWrapper (the element to which we apply translate+scale).
   * Because the wrapper is transformed (scale/translate), getBoundingClientRect of nodes and the wrapper reflect the transform.
   * Let:
   *   screenCoord = elemRect - wrapperRect
   * Then the SVG inside wrapper is also transformed; to supply coordinates in SVG user units:
   *   userCoord = screenCoord / zoom
   * Also set svg width/height = wrapperRect.width/zoom, wrapperRect.height/zoom
   */
  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const wrapper = scaleWrapperRef.current;
    if (!scrollEl || !wrapper) return;

    // ensure container padding exists (fallback)
    const containerEl = containerRef.current;
    if (containerEl && !containerEl.style.padding) containerEl.style.padding = "40px";

    let mounted = true;

    const computeAll = () => {
      if (!mounted) return;
      const wrapperRect = wrapper.getBoundingClientRect();

      const newPaths = [];
      for (const conn of connectionsRef.current) {
        const fromEl = nodeRefs.current[conn.fromKey];
        const toEl = nodeRefs.current[conn.toKey];
        if (!fromEl || !toEl) continue;

        const fRect = fromEl.getBoundingClientRect();
        const tRect = toEl.getBoundingClientRect();

        // screen coords relative to wrapper top-left
        const sx1 = fRect.left + fRect.width / 2 - wrapperRect.left;
        const sy1 = fRect.bottom - wrapperRect.top;
        const sx2 = tRect.left + tRect.width / 2 - wrapperRect.left;
        const sy2 = tRect.top - wrapperRect.top;

        // convert to SVG user coords (before wrapper scale): divide by zoom
        const x1 = sx1 / zoom;
        const y1 = sy1 / zoom;
        const x2 = sx2 / zoom;
        const y2 = sy2 / zoom;

        const midY = (y1 + y2) / 2;
        newPaths.push(`M ${x1} ${y1} C ${x1} ${midY} ${x2} ${midY} ${x2} ${y2}`);
      }

      // size svg to wrapper size in user coordinates (wrapperRect / zoom)
      const svgW = wrapperRect.width / zoom;
      const svgH = wrapperRect.height / zoom;

      if (!mounted) return;
      setPaths(newPaths);
      setSvgSize({ width: svgW, height: svgH });
    };

    computeAll();
    const raf = requestAnimationFrame(computeAll);

    const onScroll = () => requestAnimationFrame(computeAll);
    const onResize = () => requestAnimationFrame(computeAll);

    // re-run when outer scroll or window resize occurs
    scrollEl.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [root, zoom, pan]); // recompute when zoom or pan changes

  // transform applied to wrapper (translate + scale)
  const transformStyle = { transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "0 0" };

  return (
    <div className="relative overflow-hidden">
      {/* controls */}
      <div className="absolute top-3 right-3 z-20 flex gap-2">
        <button onClick={handleZoomIn} className="p-2 bg-white border rounded shadow-sm"><ZoomIn className="  h-4 text-gray-600" /></button>
        <button onClick={handleZoomOut} className="p-2 bg-white border rounded shadow-sm"><ZoomOut className="  h-4 text-gray-600" /></button>
        <button onClick={handleReset} className="p-2 bg-white border rounded shadow-sm"> <RotateCcw className="  h-4 text-gray-600" /></button>
      </div>

      {/* main scroll wrapper (outer) */}
      <div
        ref={scrollRef}
        className="bg-white rounded-sm border border-gray-200 cursor-grab active:cursor-grabbing"
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={(e) => onPointerDown(e.touches ? e.touches[0] : e)}
        onTouchMove={(e) => onPointerMove(e.touches ? e.touches[0] : e)}
        onTouchEnd={onPointerUp}
      >
        <div className="dtv-root">
          <div className="dtv-container" ref={containerRef}>
            {/* wrapper that receives pan+zoom transform */}
            <div ref={scaleWrapperRef} className="dtv-scale-wrapper" style={transformStyle}>
              <div className="dtv-center">
                <RenderNode node={root} path="root" visited={new Set()} />
              </div>

              {/* SVG sized in user coordinates (wrapperRect/zoom) and placed at top-left of wrapper */}
              <svg
                className="dtv-svg w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
                width={svgSize.width}
                height={svgSize.height}
                style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}
              >
                <g>
                  {paths.map((p, i) => (
                    <path key={i} d={p} className="dtv-connector" />
                  ))}
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownwardTreeView;
